import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ChevronDown,
  CircleUser,
  LayoutGrid,
  Loader2,
  Network,
  RefreshCw,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import {
  RelationshipProviders,
  useRole,
} from "./account-relationships/RelationshipContexts";
import { isAccountTeamVendorLabel } from "./account-relationships/relationshipUtils";
import AccountRelationshipsShell from "./AccountRelationshipsShell";
import CoverageReturnBanner from "../CoverageAnalysis/CoverageReturnBanner";
import {
  canViewMyRelationshipsTab,
  canViewMyPortfolioToggle,
} from "./my-relationships/relationshipTabAccess";
import MyRelationshipsShell from "./MyRelationshipsShell";
import PortfolioOverviewShell from "./PortfolioOverviewShell";
import RelationshipAccountsLoader from "./RelationshipAccountsLoader";
import SponsorStakeholderCard from "./SponsorStakeholderCard";
import SponsorStakeholderTab from "./SponsorStakeholderTab";
import {
  resolveEffectiveAccountUserIds,
  resolveLoggedInUserId,
} from "./stakeholderPortfolioScopeUtils";
import Button from "../../Components/Button";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchRelationshipAssessmentAccounts,
  fetchRelationshipAssessmentStakeholders,
  loadAccountTeamMembers,
  setSelectedStakeholderUserIds,
  setViewMyPortfolio,
} from "../../Store/Reducers/RelationshipSlice";
import type {
  RelationshipAccountSummary,
  RelationshipAssessmentStakeholder,
} from "../../Store/Reducers/RelationshipSlice";
import {
  fetchOrganizationSettings,
  selectOrganizationSettingsAccess,
} from "../../Store/Reducers/VendorAdminSlice";
import {
  getDefaultRouteForRole,
  isRole,
  ROLES,
  TEAM_VIEWS_ROUTE_PATH,
} from "../../Utils/Constants/RoleConstants";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

import "./account-relationships/relationship-prototype-skin.css";

type RelationshipTab =
  | "portfolio-overview"
  | "account-relationships"
  | "my-relationships";

const RELATIONSHIP_TABS: RelationshipTab[] = [
  "portfolio-overview",
  "account-relationships",
  "my-relationships",
];

type TeamAccountGroup = {
  id: string;
  name: string;
  users: RelationshipAssessmentStakeholder[];
};

const parseRelationshipDeepLink = (search: string) => {
  const params = new URLSearchParams(search);
  const tabParam = params.get("tab");
  const normalizedTab =
    tabParam === "sponsor-stakeholder" ? "portfolio-overview" : tabParam;
  const tab =
    normalizedTab &&
    RELATIONSHIP_TABS.includes(normalizedTab as RelationshipTab)
      ? (normalizedTab as RelationshipTab)
      : "portfolio-overview";
  const accountId = params.get("accountId");
  return {
    tab,
    accountId: accountId || null,
  };
};

const RelationshipContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const deepLink = useMemo(
    () => parseRelationshipDeepLink(location.search),
    [location.search]
  );
  const [focusPair, setFocusPair] = useState<{
    vendorId: string;
    contactId: string;
  } | null>(null);
  const { rlAssessmentsEnabled } = useRole();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get("tab");
    if (tabParam !== "team-views") return;
    const slug = loggedInUser?.slug;
    const target =
      slug && !isRole(loggedInUser?.role, ROLES.IZBA_ADMIN)
        ? `/${slug}${TEAM_VIEWS_ROUTE_PATH}`
        : TEAM_VIEWS_ROUTE_PATH;
    navigate(target, { replace: true });
  }, [location.search, loggedInUser?.role, loggedInUser?.slug, navigate]);
  const myRelationshipsVisible = useMemo(
    () => canViewMyRelationshipsTab(loggedInUser?.role),
    [loggedInUser?.role]
  );
  const myPortfolioToggleVisible = useMemo(
    () => canViewMyPortfolioToggle(loggedInUser?.role),
    [loggedInUser?.role]
  );
  const { resolved: organizationSettingsResolved } = useAppSelector(
    selectOrganizationSettingsAccess
  );
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    loggedInUser?.role
  );
  const [activeTab, setActiveTab] = useState<RelationshipTab>(deepLink.tab);
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string[]>([]);
  const [preferredAccountId, setPreferredAccountId] = useState<string | null>(
    deepLink.accountId
  );
  const [myRelationshipsRefreshKey, setMyRelationshipsRefreshKey] = useState(0);
  const [teamsRelationshipToggle, setTeamsRelationshipToggle] = useState(false);
  const [teamRelModalOpen, setTeamRelModalOpen] = useState(false);
  const [teamRelLoading, setTeamRelLoading] = useState(false);
  const [teamRelGroups, setTeamRelGroups] = useState<TeamAccountGroup[]>([]);
  const [teamRelPendingUserId, setTeamRelPendingUserId] = useState<
    string | null
  >(null);
  const [teamMemberAccountIds, setTeamMemberAccountIds] = useState<string[]>(
    []
  );
  const [teamsSelectedUserId, setTeamsSelectedUserId] = useState<string | null>(
    null
  );
  const portfolioScopeSnapshotRef = useRef<{
    selectedIds: string[];
    viewMyPortfolio: boolean;
  } | null>(null);

  const {
    assessmentStakeholders,
    assessmentStakeholdersLoading,
    relationshipAccountsLoading,
    myRelationshipAccountsLoading,
    myRelationshipMappingsLoading,
    selectedStakeholderUserIds,
    myRelationshipAccounts,
    viewMyPortfolio,
    error,
  } = useAppSelector(state => state.relationship);

  const loggedInUserId = resolveLoggedInUserId(loggedInUser);
  const stakeholders = assessmentStakeholders?.users ?? [];

  const handleTabChange = useCallback(
    (nextTab: RelationshipTab) => {
      if (nextTab === activeTab) return;

      if (nextTab === "my-relationships") {
        setTeamsRelationshipToggle(false);
        portfolioScopeSnapshotRef.current = {
          selectedIds: selectedStakeholderUserIds,
          viewMyPortfolio,
        };
        if (loggedInUserId) {
          dispatch(setSelectedStakeholderUserIds([loggedInUserId]));
          dispatch(setViewMyPortfolio(false));
        }
      } else if (activeTab === "my-relationships") {
        const snapshot = portfolioScopeSnapshotRef.current;
        portfolioScopeSnapshotRef.current = null;
        if (snapshot) {
          dispatch(setSelectedStakeholderUserIds(snapshot.selectedIds));
          dispatch(setViewMyPortfolio(snapshot.viewMyPortfolio));
        }
      }

      setActiveTab(nextTab);
    },
    [
      activeTab,
      dispatch,
      loggedInUserId,
      selectedStakeholderUserIds,
      viewMyPortfolio,
    ]
  );

  useEffect(() => {
    handleTabChange(deepLink.tab);
    if (deepLink.accountId) {
      setPreferredAccountId(deepLink.accountId);
    }
    // Only sync tab from URL when the URL changes, not on every activeTab change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink.tab, deepLink.accountId]);

  // One-time matrix blink from My Accounts — take location.state, then drop it.
  useEffect(() => {
    const state = (location.state || {}) as {
      focusVendorId?: string | null;
      focusContactId?: string | null;
    };
    const vendorId = state.focusVendorId ? String(state.focusVendorId) : "";
    const contactId = state.focusContactId ? String(state.focusContactId) : "";
    if (!vendorId || !contactId) return;
    setFocusPair({ vendorId, contactId });
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} }
    );
  }, [location.state, location.pathname, location.search, navigate]);

  const handleFocusConsumed = useCallback(() => setFocusPair(null), []);

  const teamsSelectedUserInfo = useMemo(
    () =>
      teamsSelectedUserId
        ? (teamRelGroups
            .flatMap(g => g.users)
            .find(u => u.user_id === teamsSelectedUserId) ?? null)
        : null,
    [teamRelGroups, teamsSelectedUserId]
  );

  const tabs = useMemo(() => {
    if (!rlAssessmentsEnabled) return [];
    const tabIconClass = "h-4 w-4";
    return [
      {
        id: "portfolio-overview",
        label: "Portfolio Overview",
        icon: <LayoutGrid className={tabIconClass} aria-hidden />,
      },
      {
        id: "account-relationships",
        label: "Account Relationships",
        icon: <Network className={tabIconClass} aria-hidden />,
      },
      ...(myRelationshipsVisible
        ? [
            {
              id: "my-relationships",
              label: "My Relationships",
              icon: <CircleUser className={tabIconClass} aria-hidden />,
            },
          ]
        : []),
    ];
  }, [rlAssessmentsEnabled, myRelationshipsVisible]);

  const loadStakeholders = useCallback(() => {
    dispatch(fetchRelationshipAssessmentStakeholders(undefined));
  }, [dispatch]);

  const loadAccounts = useCallback(
    (force = false) => {
      if (!rlAssessmentsEnabled) return;
      const effectiveUserIds = resolveEffectiveAccountUserIds(
        selectedStakeholderUserIds,
        viewMyPortfolio,
        { user_id: loggedInUserId, role: loggedInUser?.role }
      );
      dispatch(
        fetchRelationshipAssessmentAccounts({
          force,
          userIds: effectiveUserIds,
        })
      );
    },
    [
      dispatch,
      loggedInUser?.role,
      loggedInUserId,
      rlAssessmentsEnabled,
      selectedStakeholderUserIds,
      viewMyPortfolio,
    ]
  );

  useEffect(() => {
    loadStakeholders();
  }, [loadStakeholders]);

  useEffect(() => {
    if (!myRelationshipsVisible && activeTab === "my-relationships") {
      setActiveTab("portfolio-overview");
    }
  }, [activeTab, myRelationshipsVisible]);

  // One-time default when stakeholders first load on portfolio tabs.
  const portfolioScopeInitializedRef = useRef(false);
  useEffect(() => {
    if (
      !stakeholders.length ||
      portfolioScopeInitializedRef.current ||
      activeTab === "my-relationships"
    ) {
      return;
    }

    portfolioScopeInitializedRef.current = true;

    if (myPortfolioToggleVisible && loggedInUserId) {
      dispatch(setSelectedStakeholderUserIds([loggedInUserId]));
      dispatch(setViewMyPortfolio(true));
      return;
    }

    dispatch(setSelectedStakeholderUserIds(stakeholders.map(s => s.user_id)));
    dispatch(setViewMyPortfolio(false));
  }, [
    activeTab,
    stakeholders,
    dispatch,
    myPortfolioToggleVisible,
    loggedInUserId,
  ]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchOrganizationSettings());
    loadStakeholders();
    if (
      rlAssessmentsEnabled &&
      (activeTab === "portfolio-overview" ||
        activeTab === "account-relationships")
    ) {
      loadAccounts(true);
    }
    if (rlAssessmentsEnabled && activeTab === "my-relationships") {
      setMyRelationshipsRefreshKey(key => key + 1);
    }
  }, [
    activeTab,
    dispatch,
    loadAccounts,
    loadStakeholders,
    rlAssessmentsEnabled,
  ]);

  const handleNavigateToAccountRelationships = useCallback(
    (account: RelationshipAccountSummary) => {
      setPreferredAccountId(account.id);
      handleTabChange("account-relationships");
    },
    [handleTabChange]
  );

  const handleNavigateToPortfolioOverview = useCallback(() => {
    handleTabChange("portfolio-overview");
  }, [handleTabChange]);

  const allStakeholdersSelected =
    stakeholders.length > 0 &&
    selectedStakeholderUserIds.length === stakeholders.length;

  const myPortfolioActive =
    Boolean(loggedInUserId) &&
    selectedStakeholderUserIds.length === 1 &&
    selectedStakeholderUserIds[0] === loggedInUserId;

  const pendingMyPortfolioActive =
    Boolean(loggedInUserId) &&
    pendingSelection.length === 1 &&
    pendingSelection[0] === loggedInUserId;

  const singleUserSelected = selectedStakeholderUserIds.length === 1;

  const portfolioGridOwnerLabel = useMemo(() => {
    if (allStakeholdersSelected) return "All Users";
    if (selectedStakeholderUserIds.length === 1) {
      if (selectedStakeholderUserIds[0] === loggedInUserId) return "Your";
      const match = stakeholders.find(
        s => s.user_id === selectedStakeholderUserIds[0]
      );
      const firstName = (match?.user_name ?? "").trim().split(/\s+/)[0] || "";
      return firstName ? `${firstName}'s` : "Their";
    }
    const count = selectedStakeholderUserIds.length;
    return count > 1 ? `${count} Users` : "All Users";
  }, [
    allStakeholdersSelected,
    selectedStakeholderUserIds,
    loggedInUserId,
    stakeholders,
  ]);

  const selectedUserDisplay = useMemo(() => {
    if (allStakeholdersSelected) return "All Users";
    if (selectedStakeholderUserIds.length === 1) {
      if (selectedStakeholderUserIds[0] === loggedInUserId) return "You";
      const match = stakeholders.find(
        s => s.user_id === selectedStakeholderUserIds[0]
      );
      return match?.user_name ?? null;
    }
    if (selectedStakeholderUserIds.length > 1) {
      return `${selectedStakeholderUserIds.length} Users`;
    }
    return null;
  }, [
    allStakeholdersSelected,
    loggedInUserId,
    selectedStakeholderUserIds,
    stakeholders,
  ]);

  const refreshLoading =
    assessmentStakeholdersLoading ||
    (rlAssessmentsEnabled &&
      (activeTab === "portfolio-overview" ||
        activeTab === "account-relationships") &&
      relationshipAccountsLoading) ||
    (rlAssessmentsEnabled &&
      activeTab === "my-relationships" &&
      (myRelationshipAccountsLoading || myRelationshipMappingsLoading));

  const renderTabContent = () => {
    if (!organizationSettingsResolved) {
      return (
        <RelationshipAccountsLoader label="Loading relationship assessment settings…" />
      );
    }

    if (!rlAssessmentsEnabled) {
      return null;
    }

    if (activeTab === "portfolio-overview") {
      return (
        <PortfolioOverviewShell
          onNavigateToAccountRelationships={
            handleNavigateToAccountRelationships
          }
          portfolioGridOwnerLabel={portfolioGridOwnerLabel}
          myPortfolioActive={myPortfolioActive}
        />
      );
    }

    if (activeTab === "my-relationships" && myRelationshipsVisible) {
      return (
        <MyRelationshipsShell
          refreshKey={myRelationshipsRefreshKey}
          onNavigateToAccountRelationships={
            handleNavigateToAccountRelationships
          }
          onNavigateToPortfolioOverview={handleNavigateToPortfolioOverview}
          teamsRelationshipToggle={teamsRelationshipToggle}
          onTeamsRelationshipToggle={checked => {
            setTeamsRelationshipToggle(checked);
            if (checked) {
              const currentUserId = selectedStakeholderUserIds[0] ?? null;
              setTeamRelPendingUserId(
                currentUserId !== loggedInUserId ? currentUserId : null
              );
              setTeamRelGroups([]);
              setTeamRelLoading(true);
              setTeamRelModalOpen(true);
              Promise.all(
                myRelationshipAccounts.map(async acct => ({
                  id: acct.id,
                  name: acct.name,
                  users: await loadAccountTeamMembers(acct.id),
                }))
              ).then(groups => {
                setTeamRelGroups(groups.filter(g => g.users.length > 0));
                setTeamRelLoading(false);
              });
            } else {
              setTeamMemberAccountIds([]);
              setTeamsSelectedUserId(null);
              if (loggedInUserId) {
                dispatch(setSelectedStakeholderUserIds([loggedInUserId]));
              }
            }
          }}
          teamsSelectedUserName={
            teamsRelationshipToggle &&
            selectedUserDisplay &&
            selectedUserDisplay !== "You"
              ? selectedUserDisplay
              : undefined
          }
          teamMemberAccountIds={teamMemberAccountIds}
          teamsSelectedUserId={teamsSelectedUserId ?? undefined}
          teamsSelectedUserInfo={teamsSelectedUserInfo ?? undefined}
          onOpenTeamModal={() => setTeamRelModalOpen(true)}
        />
      );
    }

    return (
      <AccountRelationshipsShell
        key={preferredAccountId || "account-relationships"}
        preferredAccountId={preferredAccountId}
        focusVendorId={focusPair?.vendorId ?? null}
        focusContactId={focusPair?.contactId ?? null}
        onFocusConsumed={handleFocusConsumed}
      />
    );
  };

  return (
    <div className="min-h-full bg-white mt-3">
      <div className="px-3 pt-4 md:px-4">
        <CoverageReturnBanner />
      </div>
      <PageHeader
        title="Relationships"
        icon={Users}
        breadcrumb={[]}
        subTitle="Portfolio health, account relationship coverage, and individual relationships."
        actions={[
          <Button
            key="refresh"
            variant="white"
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              hubActive ? "mr-8" : ""
            }`}
            icon={<RefreshCw className="h-4 w-4" />}
            loading={refreshLoading}
            onClick={handleRefresh}
          >
            Refresh
          </Button>,
        ]}
      />

      <div className="w-full bg-white px-3 pb-4 pt-3 md:px-4 md:pb-6 md:pt-4">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-3 bg-gray-50 border-b border-gray-200 px-4 py-3 overflow-visible">
            <div className="flex-1 overflow-visible">
              <TabsComponent
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={tabId => handleTabChange(tabId as RelationshipTab)}
                variant="tab-segmented"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {activeTab === "my-relationships" ? null : (
                <>
                  {myPortfolioToggleVisible ? (
                    <ToggleSwitch
                      id="rl-my-portfolio-toggle-main"
                      label="My Portfolio"
                      checked={myPortfolioActive}
                      onChange={checked => {
                        if (checked && loggedInUserId) {
                          dispatch(
                            setSelectedStakeholderUserIds([loggedInUserId])
                          );
                        } else {
                          dispatch(
                            setSelectedStakeholderUserIds(
                              stakeholders.map(s => s.user_id)
                            )
                          );
                        }
                        dispatch(setViewMyPortfolio(checked));
                      }}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setPendingSelection([...selectedStakeholderUserIds]);
                      setSelectorModalOpen(true);
                    }}
                    className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm shadow-sm transition-colors ${
                      singleUserSelected
                        ? "border-violet-500 bg-violet-50 hover:bg-violet-100"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <UserCheck
                      className={`h-4 w-4 shrink-0 ${singleUserSelected ? "text-violet-600" : "text-gray-500"}`}
                    />
                    <span
                      className={`text-xs font-medium ${singleUserSelected ? "text-violet-600" : "text-gray-400"}`}
                    >
                      Select User
                    </span>
                    {selectedUserDisplay ? (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="font-semibold text-gray-800 max-w-[140px] truncate">
                          {selectedUserDisplay}
                        </span>
                      </>
                    ) : null}
                    <ChevronDown
                      className={`h-3.5 w-3.5 shrink-0 ${singleUserSelected ? "text-violet-400" : "text-gray-400"}`}
                    />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white p-4 min-h-[320px]">{renderTabContent()}</div>
        </div>
      </div>

      {selectorModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1500px] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">
                Select User
              </h2>
              <div className="flex items-center gap-4">
                {activeTab !== "my-relationships" &&
                myPortfolioToggleVisible ? (
                  <ToggleSwitch
                    id="rl-my-portfolio-toggle-modal"
                    label="My Portfolio"
                    checked={pendingMyPortfolioActive}
                    onChange={checked => {
                      if (checked && loggedInUserId) {
                        setPendingSelection([loggedInUserId]);
                      } else {
                        setPendingSelection(stakeholders.map(s => s.user_id));
                      }
                    }}
                  />
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSelectorModalOpen(false);
                    if (activeTab === "my-relationships") {
                      setTeamsRelationshipToggle(false);
                      if (loggedInUserId) {
                        dispatch(
                          setSelectedStakeholderUserIds([loggedInUserId])
                        );
                      }
                    }
                  }}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <SponsorStakeholderTab
                stakeholders={stakeholders}
                loading={assessmentStakeholdersLoading}
                error={error}
                loggedInUserId={loggedInUserId}
                externalSelectedIds={pendingSelection}
                onExternalSelectionChange={ids => setPendingSelection(ids)}
                singleSelectMode={activeTab === "my-relationships"}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end shrink-0">
              <Button
                variant="primary"
                onClick={() => {
                  dispatch(setSelectedStakeholderUserIds(pendingSelection));
                  dispatch(setViewMyPortfolio(pendingMyPortfolioActive));
                  setSelectorModalOpen(false);
                  if (
                    activeTab === "my-relationships" &&
                    pendingSelection.length === 1 &&
                    pendingSelection[0] === loggedInUserId
                  ) {
                    setTeamsRelationshipToggle(false);
                  }
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {teamRelModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1500px] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">
                Team Relationship — Pick a Team Member
              </h2>
              <button
                type="button"
                onClick={() => {
                  setTeamRelModalOpen(false);
                  setTeamsRelationshipToggle(false);
                  if (loggedInUserId) {
                    dispatch(setSelectedStakeholderUserIds([loggedInUserId]));
                  }
                }}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto p-6">
              {teamRelLoading ? (
                <div
                  className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <Loader2
                    className="h-8 w-8 animate-spin text-violet-600"
                    aria-hidden
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Loading team members…
                  </span>
                </div>
              ) : teamRelGroups.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <span className="text-sm text-gray-400">
                    No team members found in your portfolio accounts.
                  </span>
                </div>
              ) : (
                <div className="space-y-6">
                  {teamRelGroups.map(group => {
                    const groupUsers = group.users.filter(
                      u => u.user_id !== loggedInUserId
                    );
                    const accountTeamUsers = groupUsers.filter(u =>
                      isAccountTeamVendorLabel(u.label)
                    );
                    const execTeamUsers = groupUsers.filter(
                      u => !isAccountTeamVendorLabel(u.label)
                    );

                    const renderUserRow = (
                      users: RelationshipAssessmentStakeholder[],
                      rowKey: string
                    ) => (
                      <div className="flex gap-3 overflow-x-auto pb-1">
                        {users.map(user => (
                          <div
                            key={`${rowKey}-${user.user_id}`}
                            className="w-[200px] shrink-0"
                          >
                            <SponsorStakeholderCard
                              stakeholder={user}
                              isSelected={teamRelPendingUserId === user.user_id}
                              onToggle={() =>
                                setTeamRelPendingUserId(
                                  teamRelPendingUserId === user.user_id
                                    ? null
                                    : user.user_id
                                )
                              }
                            />
                          </div>
                        ))}
                      </div>
                    );

                    return (
                      <div key={group.id}>
                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-100">
                          <div
                            className="h-7 w-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
                            style={{ background: "#1e293b" }}
                          >
                            {group.name.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {group.name}
                          </span>
                        </div>
                        <div className="pl-10 space-y-4">
                          {execTeamUsers.length > 0 ? (
                            <div>
                              <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                Exec Team:
                              </span>
                              {renderUserRow(execTeamUsers, "exec")}
                            </div>
                          ) : null}
                          {accountTeamUsers.length > 0 ? (
                            <div>
                              <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                Account Team:
                              </span>
                              {renderUserRow(accountTeamUsers, "account")}
                            </div>
                          ) : null}
                          {groupUsers.length === 0 ? (
                            <div className="text-sm text-gray-400 py-2">
                              No team members found.
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
              <span className="text-sm text-gray-400">
                {teamRelPendingUserId
                  ? (() => {
                      const found = teamRelGroups
                        .flatMap(g => g.users)
                        .find(u => u.user_id === teamRelPendingUserId);
                      return found ? `Selected: ${found.user_name}` : "";
                    })()
                  : "No user selected"}
              </span>
              <Button
                variant="primary"
                disabled={!teamRelPendingUserId}
                title={
                  teamRelPendingUserId
                    ? undefined
                    : "Select a team member to apply"
                }
                onClick={() => {
                  if (!teamRelPendingUserId) return;
                  const sharedIds = teamRelGroups
                    .filter(g =>
                      g.users.some(u => u.user_id === teamRelPendingUserId)
                    )
                    .map(g => g.id);
                  setTeamMemberAccountIds(sharedIds);
                  setTeamsSelectedUserId(teamRelPendingUserId);
                  dispatch(
                    setSelectedStakeholderUserIds([teamRelPendingUserId])
                  );
                  setTeamRelModalOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RelationshipPage: React.FC = () => (
  <RelationshipProviders>
    <RelationshipContent />
  </RelationshipProviders>
);

const RelationshipRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const { resolved, loading, enabled, error } = useAppSelector(
    selectOrganizationSettingsAccess
  );
  const organizationSettingsLoading = useAppSelector(
    state => state.vendorAdmin.organizationSettingsLoading
  );

  if (loading || !resolved || (error && organizationSettingsLoading)) {
    return (
      <RelationshipAccountsLoader label="Loading relationships…" centered />
    );
  }

  if (error) {
    return (
      <div className="rl-prototype-skin flex min-h-[calc(100vh-10rem)] w-full flex-col items-center justify-center gap-3 bg-white px-6 text-center text-sm text-gray-600">
        <p>Unable to verify relationship assessment access.</p>
        <Button
          variant="primary"
          onClick={() => dispatch(fetchOrganizationSettings())}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!enabled) {
    const defaultRoute = getDefaultRouteForRole(loggedInUser?.role);
    const tenantSlug = loggedInUser?.slug;
    const targetPath =
      tenantSlug && !isRole(loggedInUser?.role, ROLES.IZBA_ADMIN)
        ? `/${tenantSlug}${defaultRoute}`
        : defaultRoute;
    return <Navigate to={targetPath} replace />;
  }

  return <RelationshipPage />;
};

export default RelationshipRoute;
